import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  BarChart,
  Bar,
  Line,
} from 'recharts'

import Page from '../Shared/Page'
import { aiAssistantAxios } from '../../utils/io/http/axios'
import exportPdf from '../../utils/exportPdf'
import './ChatGptMcpAnalytics.scss'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Totals {
  total_calls: number
  unique_users: number
  success_rate_pct: number
  ai_calls: number
  crud_calls: number
  read_calls: number
}

interface DailyUsage {
  date: string
  total_calls: number
  unique_users: number
  ai_calls: number
  crud_calls: number
  read_calls: number
  error_count: number
}

interface ToolBreakdown {
  tool_name: string
  category: string
  call_count: number
  success_count: number
  error_count: number
  avg_duration_ms: number
}

interface TopUser {
  userId: number
  email: string
  name: string
  total_calls: number
  ai_calls: number
  crud_calls: number
}

interface TopApp {
  appId: string
  app_name: string
  total_calls: number
}

interface Pagination {
  offset: number
  limit: number
  total: number
}

interface ToolUsageData {
  totals: Totals
  daily: DailyUsage[]
  by_tool: ToolBreakdown[]
  top_users: TopUser[]
  top_users_pagination: Pagination
  top_apps: TopApp[]
  top_apps_pagination: Pagination
}

interface ErrorSummary {
  total_calls: number
  total_errors: number
  error_rate_pct: number
}

interface DailyError {
  date: string
  total_calls: number
  error_count: number
  error_rate_pct: number
}

interface ErrorByTool {
  tool_name: string
  total_calls: number
  error_count: number
  error_rate_pct: number
}

interface TopError {
  error_snippet: string
  tool_name: string
  occurrences: number
}

interface ErrorData {
  summary: ErrorSummary
  daily: DailyError[]
  by_tool: ErrorByTool[]
  top_errors: TopError[]
}

interface CreateAppStats {
  total_initiated: number
  total_completed: number
  total_failed: number
  avg_duration_ms: number | null
  daily: {
    date: string
    initiated: number
    completed: number
    failed: number
  }[]
}

interface ModifyAppStats {
  total_calls: number
  success_count: number
  avg_duration_ms: number | null
  top_actions: { action: string; count: number }[]
  daily: { date: string; calls: number; successes: number }[]
}

interface AppOpsData {
  create_app: CreateAppStats
  modify_app: ModifyAppStats
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDuration = (ms: number | null) => {
  if (ms === null || ms === undefined) return '—'
  if (ms < 1000) return `${ms}ms`

  return `${(ms / 1000).toFixed(1)}s`
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const SummaryCards = ({ totals }: { totals: Totals }) => (
  <div className="stats-cards">
    <div className="stat-card">
      <div className="stat-label">Total Calls</div>
      <div className="stat-value">{totals.total_calls.toLocaleString()}</div>
    </div>
    <div className="stat-card">
      <div className="stat-label">Unique Users</div>
      <div className="stat-value">{totals.unique_users}</div>
    </div>
    <div className="stat-card">
      <div className="stat-label">Success Rate</div>
      <div className="stat-value">{totals.success_rate_pct ?? 0}%</div>
    </div>
    <div className="stat-card">
      <div className="stat-label">AI Calls</div>
      <div className="stat-value" style={{ color: '#7c3aed' }}>
        {totals.ai_calls}
      </div>
    </div>
    <div className="stat-card">
      <div className="stat-label">CRUD Calls</div>
      <div className="stat-value" style={{ color: '#2563eb' }}>
        {totals.crud_calls}
      </div>
    </div>
    <div className="stat-card">
      <div className="stat-label">Read Calls</div>
      <div className="stat-value" style={{ color: '#6b7280' }}>
        {totals.read_calls}
      </div>
    </div>
  </div>
)

const DailyUsageChart = ({ data }: { data: DailyUsage[] }) => (
  <div className="section">
    <h2>Daily Usage</h2>
    <div className="chart-container">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          <Line
            type="monotone"
            dataKey="total_calls"
            name="Total"
            stroke="#111827"
            strokeWidth={2}
          />
          <Line type="monotone" dataKey="ai_calls" name="AI" stroke="#7c3aed" />
          <Line
            type="monotone"
            dataKey="crud_calls"
            name="CRUD"
            stroke="#2563eb"
          />
          <Line
            type="monotone"
            dataKey="read_calls"
            name="Read"
            stroke="#9ca3af"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
)

const ToolBreakdownTable = ({ data }: { data: ToolBreakdown[] }) => (
  <div className="section">
    <h2>Tool Breakdown</h2>
    <table className="analytics-table">
      <thead>
        <tr>
          <th>Tool</th>
          <th>Category</th>
          <th>Calls</th>
          <th>Success</th>
          <th>Errors</th>
          <th>Error %</th>
          <th>Avg Duration</th>
        </tr>
      </thead>
      <tbody>
        {data.map(t => (
          <tr key={t.tool_name}>
            <td>
              <strong>{t.tool_name}</strong>
            </td>
            <td>
              <span className={`category-badge ${t.category}`}>
                {t.category}
              </span>
            </td>
            <td>{t.call_count}</td>
            <td>{t.success_count}</td>
            <td>{t.error_count}</td>
            <td>
              {t.call_count > 0
                ? ((t.error_count / t.call_count) * 100).toFixed(1)
                : 0}
              %
            </td>
            <td>{formatDuration(t.avg_duration_ms)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

const ErrorRateSection = ({
  errors,
  unavailable,
}: {
  errors: ErrorData | null
  unavailable: boolean
}) => (
  <div className="section">
    <h2>Error Rate</h2>
    {unavailable ? (
      <div className="section-unavailable">Error data unavailable.</div>
    ) : (
      <>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={errors?.daily || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="error_count" name="Errors" fill="#ef4444" />
              <Bar dataKey="total_calls" name="Total" fill="#d1d5db" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {(errors?.top_errors || []).length > 0 && (
          <>
            <h3>Top Errors</h3>
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Error</th>
                  <th>Tool</th>
                  <th>Occurrences</th>
                </tr>
              </thead>
              <tbody>
                {(errors?.top_errors || []).map(e => (
                  <tr key={`${e.tool_name}-${e.error_snippet}`}>
                    <td>{e.error_snippet}</td>
                    <td>{e.tool_name}</td>
                    <td>{e.occurrences}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </>
    )}
  </div>
)

const AppOperationsSection = ({
  appOps,
  unavailable,
}: {
  appOps: AppOpsData | null
  unavailable: boolean
}) => (
  <div className="section">
    <h2>App Operations</h2>
    {unavailable ? (
      <div className="section-unavailable">
        App operations data unavailable.
      </div>
    ) : (
      <>
        <div className="subsection">
          <h3>create_app</h3>
          <div className="stats-cards" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="stat-label">Initiated</div>
              <div className="stat-value">
                {appOps?.create_app.total_initiated ?? 0}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Completed</div>
              <div className="stat-value" style={{ color: '#10b981' }}>
                {appOps?.create_app.total_completed ?? 0}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Failed</div>
              <div className="stat-value" style={{ color: '#ef4444' }}>
                {appOps?.create_app.total_failed ?? 0}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Avg Duration</div>
              <div className="stat-value">
                {formatDuration(appOps?.create_app.avg_duration_ms ?? null)}
              </div>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={appOps?.create_app.daily || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="initiated" name="Initiated" fill="#6366f1" />
                <Bar dataKey="completed" name="Completed" fill="#10b981" />
                <Bar dataKey="failed" name="Failed" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="subsection">
          <h3>modify_app</h3>
          <div className="stats-cards" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="stat-label">Total Calls</div>
              <div className="stat-value">
                {appOps?.modify_app.total_calls ?? 0}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Successes</div>
              <div className="stat-value" style={{ color: '#10b981' }}>
                {appOps?.modify_app.success_count ?? 0}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Avg Duration</div>
              <div className="stat-value">
                {formatDuration(appOps?.modify_app.avg_duration_ms ?? null)}
              </div>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={appOps?.modify_app.daily || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="calls" name="Calls" fill="#6366f1" />
                <Bar dataKey="successes" name="Successes" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {(appOps?.modify_app.top_actions || []).length > 0 && (
            <>
              <h3>Top Actions</h3>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {(appOps?.modify_app.top_actions || []).map(a => (
                    <tr key={a.action}>
                      <td>{a.action}</td>
                      <td>{a.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </>
    )}
  </div>
)

const TopUsersSection = ({
  users,
  pagination,
  offset,
  onOffsetChange,
  unavailable,
}: {
  users: TopUser[]
  pagination: Pagination | undefined
  offset: number
  onOffsetChange: (offset: number) => void
  unavailable: boolean
}) => (
  <div className="section">
    <h2>Top Users</h2>
    {unavailable ? (
      <div className="section-unavailable">Top users data unavailable.</div>
    ) : (
      <>
        <table className="analytics-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Total</th>
              <th>AI</th>
              <th>CRUD</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.userId}>
                <td>
                  {u.name || u.email || `User #${u.userId}`}
                  {u.email && u.name ? (
                    <span
                      style={{
                        color: '#9ca3af',
                        marginLeft: 6,
                        fontSize: 12,
                      }}
                    >
                      {u.email}
                    </span>
                  ) : null}
                </td>
                <td>{u.total_calls}</td>
                <td>{u.ai_calls}</td>
                <td>{u.crud_calls}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pagination">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => onOffsetChange(Math.max(0, offset - 20))}
          >
            Prev
          </button>
          <button
            type="button"
            disabled={offset + 20 >= (pagination?.total || 0)}
            onClick={() => onOffsetChange(offset + 20)}
          >
            Next
          </button>
        </div>
      </>
    )}
  </div>
)

const TopAppsSection = ({
  apps,
  pagination,
  offset,
  onOffsetChange,
  unavailable,
}: {
  apps: TopApp[]
  pagination: Pagination | undefined
  offset: number
  onOffsetChange: (offset: number) => void
  unavailable: boolean
}) => (
  <div className="section">
    <h2>Top Apps</h2>
    {unavailable ? (
      <div className="section-unavailable">Top apps data unavailable.</div>
    ) : (
      <>
        <table className="analytics-table">
          <thead>
            <tr>
              <th>App</th>
              <th>Total Calls</th>
            </tr>
          </thead>
          <tbody>
            {apps.map(a => (
              <tr key={a.appId}>
                <td>{a.app_name || a.appId}</td>
                <td>{a.total_calls}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pagination">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => onOffsetChange(Math.max(0, offset - 20))}
          >
            Prev
          </button>
          <button
            type="button"
            disabled={offset + 20 >= (pagination?.total || 0)}
            onClick={() => onOffsetChange(offset + 20)}
          >
            Next
          </button>
        </div>
      </>
    )}
  </div>
)

// ─── Main Component ──────────────────────────────────────────────────────────

const ChatGptMcpAnalytics = () => {
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  )
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10))
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [toolUsage, setToolUsage] = useState<ToolUsageData | null>(null)
  const [toolUsageFailed, setToolUsageFailed] = useState(false)
  const [errors, setErrors] = useState<ErrorData | null>(null)
  const [errorsFailed, setErrorsFailed] = useState(false)
  const [appOps, setAppOps] = useState<AppOpsData | null>(null)
  const [appOpsFailed, setAppOpsFailed] = useState(false)
  const [topUsersOffset, setTopUsersOffset] = useState(0)
  const [topAppsOffset, setTopAppsOffset] = useState(0)
  const reportRef = useRef<HTMLDivElement>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setFetchError(null)
    setToolUsageFailed(false)
    setErrorsFailed(false)
    setAppOpsFailed(false)

    const [usageResult, errorsResult, appOpsResult] = await Promise.allSettled([
      aiAssistantAxios.get<ToolUsageData>(
        `/admin/ai/mcp/tool-usage?start_date=${startDate}&end_date=${endDate}&top_users_offset=${topUsersOffset}&top_users_limit=20&top_apps_offset=${topAppsOffset}&top_apps_limit=20`
      ),
      aiAssistantAxios.get<ErrorData>(
        `/admin/ai/mcp/errors?start_date=${startDate}&end_date=${endDate}`
      ),
      aiAssistantAxios.get<AppOpsData>(
        `/admin/ai/mcp/app-operations?start_date=${startDate}&end_date=${endDate}`
      ),
    ])

    if (usageResult.status === 'fulfilled') {
      setToolUsage(usageResult.value.data)
    } else {
      console.error('Failed to fetch tool usage:', usageResult.reason)
      setToolUsageFailed(true)
    }

    if (errorsResult.status === 'fulfilled') {
      setErrors(errorsResult.value.data)
    } else {
      console.error('Failed to fetch errors:', errorsResult.reason)
      setErrorsFailed(true)
    }

    if (appOpsResult.status === 'fulfilled') {
      setAppOps(appOpsResult.value.data)
    } else {
      console.error('Failed to fetch app operations:', appOpsResult.reason)
      setAppOpsFailed(true)
    }

    const anyFailed =
      usageResult.status === 'rejected' ||
      errorsResult.status === 'rejected' ||
      appOpsResult.status === 'rejected'
    if (anyFailed) {
      setFetchError('Some data failed to load.')
    }

    setLoading(false)
  }, [startDate, endDate, topUsersOffset, topAppsOffset])

  useEffect(() => {
    fetchData().catch(console.error)
  }, [fetchData])

  const handleExportPdf = async () => {
    if (!reportRef.current) return
    await exportPdf(reportRef.current, {
      filename: 'chatgpt-mcp-analytics.pdf',
    })
  }

  if (loading) {
    return (
      <Page title="ChatGPT MCP Analytics">
        <div className="mcp-analytics-page">
          <div className="loading">Loading analytics...</div>
        </div>
      </Page>
    )
  }

  const totals = toolUsage?.totals || {
    total_calls: 0,
    unique_users: 0,
    success_rate_pct: 0,
    ai_calls: 0,
    crud_calls: 0,
    read_calls: 0,
  }

  return (
    <Page title="ChatGPT MCP Analytics">
      <div className="mcp-analytics-page" ref={reportRef}>
        <div className="mcp-analytics-header">
          <h1>ChatGPT MCP Analytics</h1>
          <div className="header-controls">
            <div className="date-range">
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
              <span>to</span>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="primary-button"
              onClick={() => {
                handleExportPdf().catch(console.error)
              }}
            >
              Export PDF
            </button>
          </div>
        </div>

        {fetchError && (
          <div
            style={{
              color: '#b45309',
              padding: '16px',
              marginBottom: '16px',
              background: '#fffbeb',
              borderRadius: '8px',
            }}
          >
            {fetchError}
          </div>
        )}

        <SummaryCards totals={totals} />

        <DailyUsageChart data={toolUsage?.daily || []} />

        <ToolBreakdownTable data={toolUsage?.by_tool || []} />

        <ErrorRateSection errors={errors} unavailable={errorsFailed} />

        <AppOperationsSection appOps={appOps} unavailable={appOpsFailed} />

        <TopUsersSection
          users={toolUsage?.top_users || []}
          pagination={toolUsage?.top_users_pagination}
          offset={topUsersOffset}
          onOffsetChange={setTopUsersOffset}
          unavailable={toolUsageFailed}
        />

        <TopAppsSection
          apps={toolUsage?.top_apps || []}
          pagination={toolUsage?.top_apps_pagination}
          offset={topAppsOffset}
          onOffsetChange={setTopAppsOffset}
          unavailable={toolUsageFailed}
        />
      </div>
    </Page>
  )
}

export default ChatGptMcpAnalytics
